import React, { useState, useEffect } from 'react';
import { Input } from './Input';
import { useQuery } from 'services/api/hooks';
import {
  Box,
  TextFieldProps,
  List,
  ListItem,
  ListItemText,
  SxProps,
  Popover
} from '@mui/material';
import { Agent } from 'services/agents/types';
import { getAgentsByUserAndOrganization } from 'services/agents/api';
import { useRecoilValue } from 'recoil';
import { userAtom } from 'services/user/atoms';
import { organizationAtom } from 'services/organization/atom';

const listContainer: SxProps = {
  maxHeight: 300,
  overflow: 'auto',
  mt: 1,
  border: '1px solid #ccc',
  borderRadius: '4px',
  bgcolor: 'background.paper',
  p: 2,
  width: 300
};

type Props = {
  onOptionClick: (option: { value: Agent; label: string }) => void;
} & TextFieldProps;

export const AgentInput: React.FC<Props> = ({ onOptionClick, ...rest }) => {
  const [isListVisible, setIsListVisible] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );

  const user = useRecoilValue(userAtom);
  const organization = useRecoilValue(organizationAtom);

  const { onChange, value, id, name } = rest;

  const { data } = useQuery<{ items: Agent[] }>(
    getAgentsByUserAndOrganization,
    {
      userId: user?.id,
      organizationId: organization?.id
    }
  );

  const agents = data?.items;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(event);

    if (!isListVisible) {
      setIsListVisible(true);
    }

    if (!anchorEl) {
      // @ts-expect-error: //
      setAnchorEl(event.currentTarget);
    }
  };

  // Filter agents based on matching at least 2 letters from the input value
  const filteredAgents = agents
    ?.filter((agent) => {
      const inputValLower = String(value).toLowerCase();
      const agentNameLower = agent.name.toLowerCase();
      let matchCount = 0;

      for (let i = 0; i < inputValLower.length; i++) {
        if (agentNameLower.includes(inputValLower[i])) {
          matchCount++;
          if (matchCount >= 2) {
            return true;
          }
        }
      }

      return false;
    })
    .slice(0, 3);

  const handleClose = () => {
    setIsListVisible(false);
  };

  useEffect(() => {
    // Prefill the input with the first agent's name
    onChange?.({
      target: {
        value: agents?.[0]?.name || user?.name || '',
        id: agents?.[0]?.id || id || '',
        name
      }
    } as React.ChangeEvent<HTMLInputElement>);
  }, [agents]);

  return (
    <Box>
      <Input {...rest} value={value} onChange={handleInputChange} />
      <Popover
        open={!!filteredAgents?.length && isListVisible}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        disableAutoFocus={true}
        disableEnforceFocus={true}
        sx={{
          width: 400
        }}
      >
        <List sx={listContainer}>
          {filteredAgents?.map((agent) => (
            <ListItem
              button
              key={agent.id}
              onClick={() => {
                onOptionClick({
                  value: agent,
                  label: agent.name
                });
                setIsListVisible(false);
              }}
            >
              <ListItemText primary={agent.name} />
            </ListItem>
          ))}
        </List>
      </Popover>
    </Box>
  );
};
